import * as React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import {
  CategoryDistribution,
  DatasetReportItem,
  DatasetReportValue,
  DatasetSchemaItemStateType,
  DatasetType,
  NumberArrayDistribution,
  isCategoryDistribution,
  isNumberArrayDistribution
} from '../../@shared/Models/DatasetReport';
import { SchemaItem, StructureItemType } from '../../@shared/Models/Models';
import { FlexBoxColumn, FlexBoxInline } from '../../@shared/templates';
import { Palette, Sizes } from '../../@shared/palette';

export type SchemaItemReportProps = {
  schemaItem: SchemaItem,
  items: DatasetReportItem[]
}

type ValueMap = Partial<Record<DatasetSchemaItemStateType, DatasetReportValue>>;

const DatasetTypeOrder: DatasetType[] = [DatasetType.Training, DatasetType.Metrics];

const DatasetTypeLabels: Record<DatasetType, string> = {
  [DatasetType.Training]: "Training",
  [DatasetType.Metrics]: "Test"
};

// Min/Max/Avg over the full data and over the 99%/95%/90% quantile-trimmed ranges.
const RangeLabels: { label: string, suffix: string }[] = [
  { label: "All", suffix: "" },
  { label: "99%", suffix: "99" },
  { label: "95%", suffix: "95" },
  { label: "90%", suffix: "90" }
];

const numberFormat = new Intl.NumberFormat(undefined, { maximumFractionDigits: 4 });

const formatNumber = (value: DatasetReportValue | undefined) =>
  typeof value === "number" ? numberFormat.format(value) : "—";

// Scalar numbers are treated as a single-position array so both value types share one renderer.
const toPositions = (value: DatasetReportValue | undefined): NumberArrayDistribution => {
  if (isNumberArrayDistribution(value)) {
    return value;
  }

  return typeof value === "number" ? [value] : [];
};

const LabelSx = {
  color: Palette.textColorSecondary,
  fontSize: Sizes.textSecondary,
  whiteSpace: "nowrap"
};

const ValueSx = {
  color: Palette.textColorPrimary,
  fontSize: Sizes.textSecondary,
  whiteSpace: "nowrap"
};

const Stat = ({ label, value }: { label: string, value: string }) =>
  <Box sx={{ ...FlexBoxInline, gap: "4px" }}>
    <Typography sx={LabelSx}>{label}</Typography>
    <Typography sx={ValueSx}>{value}</Typography>
  </Box>;

// Number renders a single row, NumberArray one row per array position.
const NumericStats = ({ values, prefix }: { values: ValueMap, prefix: "Number" | "NumberArray" }) => {
  const [selectedSuffix, setSelectedSuffix] = React.useState<string>("");

  const available = RangeLabels.filter(range =>
    values[`${prefix}${range.suffix}Min` as DatasetSchemaItemStateType] !== undefined);
  const active = available.find(range => range.suffix === selectedSuffix) ?? available[0];

  if (!active) {
    return null;
  }

  const min = toPositions(values[`${prefix}${active.suffix}Min` as DatasetSchemaItemStateType]);
  const max = toPositions(values[`${prefix}${active.suffix}Max` as DatasetSchemaItemStateType]);
  const avg = toPositions(values[`${prefix}${active.suffix}Avg` as DatasetSchemaItemStateType]);
  const hasIndexColumn = prefix === "NumberArray";

  return <Box sx={{ ...FlexBoxColumn, gap: "5px", width: "100%" }}>
    {available.length > 1 && <Box sx={{ ...FlexBoxInline, gap: "5px", flexWrap: "wrap" }}>
      {available.map(range => <Chip
        key={range.suffix}
        size="small"
        label={range.label}
        onClick={() => setSelectedSuffix(range.suffix)}
        sx={{
          backgroundColor: range.suffix === active.suffix ? Palette.btnPrimaryHover : Palette.backgroundColorCard,
          color: Palette.textColorPrimary,
          fontSize: Sizes.textSecondary,
          border: Palette.borderPrimary
        }}
      />)}
    </Box>}
    <Box sx={{ maxHeight: "160px", overflowY: "auto", width: "100%" }}>
      <Box sx={{
        display: "grid",
        gridTemplateColumns: `${hasIndexColumn ? "32px " : ""}repeat(3, minmax(50px, 1fr))`,
        columnGap: "8px",
        rowGap: "2px"
      }}>
        {hasIndexColumn && <Typography sx={LabelSx}>#</Typography>}
        <Typography sx={LabelSx}>min</Typography>
        <Typography sx={LabelSx}>avg</Typography>
        <Typography sx={LabelSx}>max</Typography>
        {min.map((_, index) => <React.Fragment key={index}>
          {hasIndexColumn && <Typography sx={LabelSx}>{index}</Typography>}
          <Typography sx={ValueSx}>{formatNumber(min[index] ?? undefined)}</Typography>
          <Typography sx={ValueSx}>{formatNumber(avg[index] ?? undefined)}</Typography>
          <Typography sx={ValueSx}>{formatNumber(max[index] ?? undefined)}</Typography>
        </React.Fragment>)}
      </Box>
    </Box>
  </Box>;
};

const StringStats = ({ values }: { values: ValueMap }) =>
  <Box sx={{ ...FlexBoxInline, gap: "12px", flexWrap: "wrap" }}>
    <Typography sx={{ ...LabelSx, minWidth: "28px" }}>Length</Typography>
    <Stat label="min" value={formatNumber(values[DatasetSchemaItemStateType.StringLengthMin])} />
    <Stat label="avg" value={formatNumber(values[DatasetSchemaItemStateType.StringLengthAvg])} />
    <Stat label="max" value={formatNumber(values[DatasetSchemaItemStateType.StringLengthMax])} />
  </Box>;

const CategoryStats = ({ values }: { values: ValueMap }) => {
  const balance = values[DatasetSchemaItemStateType.CategoryBalance];
  const percentage = values[DatasetSchemaItemStateType.CategoryBalancePercentage];

  if (!isCategoryDistribution(balance)) {
    return null;
  }

  const percentages = isCategoryDistribution(percentage) ? percentage : {} as CategoryDistribution;

  return <Box sx={{ ...FlexBoxInline, gap: "5px", flexWrap: "wrap" }}>
    {Object.keys(balance).map(category => <Chip
      key={category}
      size="small"
      label={<>
        {category}
        <span style={{ color: Palette.textColorSecondary, fontSize: Sizes.textSecondary }}>
          {` ${formatNumber(percentages[category] ?? 0)}% (${formatNumber(balance[category])})`}
        </span>
      </>}
      sx={{
        backgroundColor: Palette.backgroundColorCard,
        color: Palette.textColorPrimary,
        fontSize: Sizes.textSecondary,
        border: Palette.borderPrimary
      }}
    />)}
  </Box>;
};

const toValueMap = (items: DatasetReportItem[], datasetType: DatasetType): ValueMap =>
  items
    .filter(x => x.datasetType === datasetType)
    .reduce<ValueMap>((map, item) => ({ ...map, [item.type]: item.value }), {});

const SchemaItemReport = ({ schemaItem, items }: SchemaItemReportProps) => {
  const sections = DatasetTypeOrder
    .map(datasetType => ({ datasetType, values: toValueMap(items, datasetType) }))
    .filter(section => Object.keys(section.values).length > 0);

  if (sections.length === 0) {
    return null;
  }

  const renderValues = (values: ValueMap) => {
    switch (schemaItem.valueTypeId) {
      case StructureItemType.Number:
        return <NumericStats values={values} prefix="Number" />;
      case StructureItemType.NumberArray:
        return <NumericStats values={values} prefix="NumberArray" />;
      case StructureItemType.String:
        return <StringStats values={values} />;
      case StructureItemType.Category:
        return <CategoryStats values={values} />;
      default:
        return null;
    }
  };

  return <Box
    sx={{
      ...FlexBoxColumn,
      gap: "6px",
      width: "100%",
      marginTop: "8px",
      paddingTop: "8px",
      borderTop: Palette.borderPrimary,
      cursor: "default"
    }}
    onClick={(e) => e.stopPropagation()}
  >
    {sections.map(section => <Box key={section.datasetType} sx={{ ...FlexBoxColumn, gap: "3px", width: "100%" }}>
      <Typography sx={{ ...LabelSx, fontWeight: 600 }}>{DatasetTypeLabels[section.datasetType]}</Typography>
      {renderValues(section.values)}
    </Box>)}
  </Box>;
};

export default SchemaItemReport;
